import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, FC, MouseEvent, ReactElement } from 'react';

import { IconButton } from '@pp/core/src/components/controls/iconButton/IconButton';
import { L10nText } from '@pp/core/src/services/uiTranslations/reactBindings/L10nText';
import { useCoreDispatch } from '@pp/core/src/store/CoreStore';
import { useModifiers } from '@pp/core/src/styles/hooks/useModifiers';

import { GameStore as store } from '../../../store/GameStore';
import { useHighflyerGameStateSlice } from '../../../store/slices/highflyerGame/selectors';
import { highflyerGameStateActions } from '../../../store/slices/highflyerGame/slice';
import { recentResultDetailStateActions } from '../../../store/slices/recentResultDetail/slice';
import { BalanceFooter } from '../../balanceFooter/BalanceFooter';
import { TableTabs } from '../../tableTabs/TableTabs';
import { TableBetsConnected } from '../tableBets/TableBetsConnected';
import { InGameHistoryConnected } from '../tableHistory/InGameHistoryConnected';
import { TableStatsConnected } from '../tableStats/TableStatsConnected';
import { TableTopConnected } from '../tableTop/TableTopConnected';

import { HideForFloatingBetBoard } from './HideForFloatingBetBoard';

import styles from './MobileTableSheet.module.css';

const componentMap: Record<string, JSX.Element> = {
  BETS_SHORT: <TableBetsConnected />,
  HISTORY: <InGameHistoryConnected />,
  STATISTICS_TOP: <TableTopConnected />,
  STATISTICS_STATS: <TableStatsConnected />,
};

export const MobileTableSheet: FC = (): ReactElement => {
  const dispatch = useCoreDispatch();
  const isExpanded = true;
  const open = useHighflyerGameStateSlice.toggleStats();

  const [delta, setDelta] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const hasMovedRef = useRef(false);
  const deltaRef = useRef<number | null>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const handleWrapperRef = useRef<HTMLDivElement>(null);
  const originRef = useRef<number | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setDelta(null);
    }
  }, [open]);

  const mods = useModifiers(styles, 'tableSheet', {
    open,
    expanded: isExpanded,
    isDragging,
  });

  const isOpenMods = useModifiers(styles, 'tableSheet__wrapper', {
    open,
  });

  const handleClose = (event: MouseEvent | TouchEvent | undefined): void => {
    if (event) {
      event.stopPropagation();
    }
    dispatch(highflyerGameStateActions.setToggleStats(false));
    dispatch(recentResultDetailStateActions.setModalOpen(false));
  };

  const handleTouchMove = (event: TouchEvent): void => {
    if (!originRef.current || !handleWrapperRef.current) return;
    event.stopPropagation();
    const touch = event.touches[0];
    const newDelta = originRef.current - touch.clientY;
    if (isExpanded && newDelta > 0) {
      setDelta(0);
    } else {
      setDelta(newDelta);
    }
    deltaRef.current = newDelta;
    hasMovedRef.current = true;
  };

  const handleTouchEnd = (): void => {
    if (!handleWrapperRef.current) return;
    const deltaValue = deltaRef.current;
    if (deltaValue !== null) {
      if ((isExpanded && deltaValue < -30) || !hasMovedRef.current || Math.abs(deltaValue) < 4) {
        dispatch(highflyerGameStateActions.setToggleStats(false));
        store.dispatch(recentResultDetailStateActions.setModalOpen(false));
      } else {
        setDelta(null);
      }
    }
    setIsDragging(false);

    handleWrapperRef.current.removeEventListener('touchmove', handleTouchMove);
    handleWrapperRef.current.removeEventListener('touchend', handleTouchEnd);

    originRef.current = null;
    deltaRef.current = null;
  };

  const handleTouchStart = (event: TouchEvent): void => {
    if (!handleWrapperRef.current) return;
    if (event.cancelable) {
      event.stopPropagation();
      event.preventDefault();
    }

    const touch = event.touches[0];
    originRef.current = touch.clientY;
    setDelta(0);
    deltaRef.current = 0;
    setIsDragging(true);

    handleWrapperRef.current.addEventListener('touchmove', handleTouchMove, { passive: true });
    handleWrapperRef.current.addEventListener('touchend', handleTouchEnd, { passive: true });
  };

  useEffect(() => {
    const handleWrapperElement = handleWrapperRef.current;
    if (!handleWrapperElement) return;

    handleWrapperElement.addEventListener('touchstart', handleTouchStart, { passive: true });

    return () => {
      handleWrapperElement.removeEventListener('touchstart', handleTouchStart);
    };
  }, [handleWrapperRef, handleTouchStart]);

  return (
    <>
      <div className={`${styles.tableSheet__wrapper} ${isOpenMods}`} ref={wrapperRef}>
        <div
          data-testid="nav-drawer-high-flyer"
          className={`${styles.tableSheet} ${mods}`}
          style={
            {
              '--delta': delta ? `${delta}px` : undefined,
            } as CSSProperties
          }
          ref={drawerRef}
        >
          <div className={styles.tableSheet__header}>
            <div className={styles.tableSheet__headerHandleWrapper} ref={handleWrapperRef}>
              <div className={styles.tableSheet__headerHandle} />
            </div>
            <div className={styles.tableSheet__headerActionContainer}></div>
            <div className={styles.tableSheet__headerTitle}>
              <L10nText tk={'STATISTICS_INFO'} />
            </div>
            <div className={styles.tableSheet__headerActionContainer}>
              <IconButton
                data-testid="drawer-close-button"
                small
                ghost
                icon="Close"
                onClick={handleClose}
                className={styles.tableSheet__headerClose}
              />
            </div>
          </div>
          {open && (
            <TableTabs
              tabs={Object.keys(componentMap).map((tab) => ({
                label: <L10nText tk={tab} />,
                content: componentMap[tab],
                key: tab,
              }))}
            />
          )}
          <div className={styles.tableSheet__footer}>
            <HideForFloatingBetBoard open={open}>
              <BalanceFooter />
            </HideForFloatingBetBoard>
          </div>
        </div>
      </div>
      <div className={styles.tableSheet__curtain} onClick={handleClose} />
    </>
  );
};
